import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Input, Table, StatusBadge } from '@/components/ui';
import { Plus, Search, Calendar, Trash2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface Event {
  id: string;
  title: string;
  slug: string;
  imageUrl?: string;
  eventDate: string;
  endDate?: string;
  ticketLink?: string;
  status: string;
  createdAt: string;
}

export function EventsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');

  const { data: events, isLoading } = useQuery<Event[]>({
    queryKey: ['events', { search, tab }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('timeframe', tab);
      const response = await api.get(`/admin/events?${params}`);
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/events/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  const columns = [
    {
      key: 'image',
      header: '',
      className: 'w-20',
      render: (event: Event) => (
        <div className="w-16 h-10 rounded overflow-hidden bg-midnight-100">
          {event.imageUrl ? (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Calendar className="h-4 w-4 text-gray-600" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Event',
      render: (event: Event) => (
        <div>
          <p className="font-medium text-white">{event.title}</p>
          <p className="text-sm text-gray-500">/{event.slug}</p>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      render: (event: Event) => (
        <div>
          <p className="text-gray-300">
            {format(new Date(event.eventDate), 'MMM d, yyyy')}
          </p>
          <p className="text-sm text-gray-500">
            {format(new Date(event.eventDate), 'h:mm a')}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (event: Event) => <StatusBadge status={event.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (event: Event) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/events/${event.id}`}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this event?')) {
                deleteMutation.mutate(event.id);
              }
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-midnight-100"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Events</h1>
          <p className="text-gray-400 mt-1">Manage special events and promotions</p>
        </div>
        <Link to="/events/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Button>
        </Link>
      </div>

      {/* Tabs & Search */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex gap-2">
            <Button
              variant={tab === 'upcoming' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setTab('upcoming')}
            >
              Upcoming
            </Button>
            <Button
              variant={tab === 'past' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setTab('past')}
            >
              Past
            </Button>
          </div>
          <div className="flex-1">
            <Input
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
        </div>
      </Card>

      {/* Events Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={events ?? []}
          keyField="id"
          loading={isLoading}
          onRowClick={(event) => navigate(`/events/${event.id}`)}
          emptyMessage="No events found"
        />
      </Card>
    </div>
  );
}
